import { useState, useEffect } from 'react';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showInstallPopup, setShowInstallPopup] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed (standalone mode)
    const checkStandalone = () => {
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                              window.navigator.standalone ||
                              document.referrer.includes('android-app://');
      setIsStandalone(isStandaloneMode);
    };

    checkStandalone();

    const handler = (e) => {
      console.log('beforeinstallprompt event fired');
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Update UI to notify the user they can install the PWA
      if (!isStandalone) {
        setShowInstallButton(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for custom beforeinstallprompt event (for development)
    const customHandler = (e) => {
      if (e.detail) {
        console.log('Custom beforeinstallprompt event received');
        handler(e.detail);
      }
    };
    window.addEventListener('beforeinstallprompt', customHandler);

    // Check if the app is already installed
    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
      setShowInstallButton(false);
      setShowInstallPopup(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    });

    // Show popup after 3 seconds if criteria are met
    const showPopupDelayed = setTimeout(() => {
      const wasDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
      const popupShown = localStorage.getItem('pwa-popup-shown') === 'true';
      if (!isStandalone && !wasDismissed && !popupShown) {
        console.log('Showing install popup after delay');
        setShowInstallPopup(true);
        localStorage.setItem('pwa-popup-shown', 'true');
      }
    }, 3000); // Show after 3 seconds

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('beforeinstallprompt', customHandler);
      clearTimeout(showPopupDelayed);
    };
  }, [isStandalone, deferredPrompt]);

  const installApp = async () => {
    console.log('Install app clicked');
    console.log('Deferred prompt available:', !!deferredPrompt);
    
    if (deferredPrompt) {
      try {
        // Show the install prompt
        const result = await deferredPrompt.prompt();
        console.log('Prompt result:', result);
        
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setShowInstallPopup(false);
          setShowInstallButton(false);
        } else {
          console.log('User dismissed the install prompt');
        }
        
        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error during installation:', error);
        // If there's an error, try to trigger the install via other means
        triggerBrowserInstallPrompt();
      }
    } else {
      // No deferred prompt available, try alternative methods
      triggerBrowserInstallPrompt();
    }
  };

  const triggerBrowserInstallPrompt = () => {
    console.log('Triggering browser install prompt alternatives');
    
    // For Chrome on Android
    if (window.navigator.standalone !== undefined) {
      // iOS Safari
      alert('To install MoodSync on iOS:\n\n' +
            '1. Tap the Share button (□↗)\n' +
            '2. Scroll down and tap "Add to Home Screen"\n' +
            '3. Tap "Add" to install the app');
      return;
    }
    
    // For desktop Chrome/Edge
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      // Modern browser - look for install icon in address bar
      const installInstructions = `To install MoodSync:\n\n` +
        `🔍 Look for the install icon (⊞ or 📱) in your browser's address bar\n` +
        `📱 Or use Chrome menu: ⋮ → "Install MoodSync..."\n\n` +
        `✨ Get the full app experience with offline access!`;
      
      alert(installInstructions);
    } else {
      // Fallback for older browsers
      alert('For the best experience, please use Chrome or Edge browser to install MoodSync as an app!');
    }
  };

  const dismissInstallPrompt = () => {
    setShowInstallButton(false);
    setShowInstallPopup(false);
    // Set a flag in localStorage to remember user dismissed the prompt
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const closeInstallPopup = () => {
    setShowInstallPopup(false);
  };

  return { showInstallButton, showInstallPopup, installApp, isStandalone, dismissInstallPrompt, closeInstallPopup };
};